import React, { createContext, useState, useContext, useCallback } from "react";
import { fetchAccounts, createAccount, updateAccount, deleteAccount, fetchAccountTypes, createAccountType,
updateAccountType, deleteAccountType} from "../api/api";
import { useAuthenticatedEffect } from "../components/useauthenticatedeffect";
import { useUser } from "../UserContext";

const AccountContext = createContext();

export const useAccounts = () => useContext(AccountContext);

export const AccountProvider = ({ children }) => {
    const [accounts, setAccounts] = useState([]);
    const [accountTypes, setAccountTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useUser();

    // method for refetching accounts and account types
    // const fetchAccountsData = useCallback(async () => {
    //     try {
    //         const accountsData = await fetchAccounts();
    //         const accountTypesData = await fetchAccountTypes();
    //         setAccounts(accountsData);
    //         setAccountTypes(accountTypesData);
    //     } catch (error) {
    //         console.error("Error loading accounts and account types", error);
    //     } finally {
    //         setLoading(false);
    //     }
    // }, []);

    // added
    const fetchAccountsData = useCallback(async () => {
        setLoading(true);
        try {
            const [accountsData, accountTypesData] = await Promise.all([
                fetchAccounts(),
                fetchAccountTypes(),
            ]);
            setAccounts(accountsData);
            setAccountTypes(accountTypesData);
        } catch (error) {
            console.error("Error loading accounts and account types", error);
        } finally {
            setLoading(false);
        }
    }, []);
    // end of added

    // use useAuthenticatedEffect instead of useEffect
    useAuthenticatedEffect(
        useCallback(() => {
            if (!user) return; // wait for user to be available
            fetchAccountsData().then(r => r); // added then(r => r) to remove warning
        }, [user, fetchAccountsData])
    );

    const loadAccountsAndTypes = useCallback(async () => {
        try {
            const accountsData = await fetchAccounts();
            // console.log("loadAccountsAndTypes returned =>", accountsData);
            const accountTypesData = await fetchAccountTypes();
            // console.log('Fetched accounts:', accountsData);
            setAccounts(accountsData);
            setAccountTypes(accountTypesData);
        } catch (error) {
            console.error("Error loading accounts and account types", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // useAuthenticatedEffect(
    //     useCallback(() => {
    //         loadAccountsAndTypes().then(r => r); // added then(r => r) to remove warning
    //         setLoading(true);
    //     }, [loadAccountsAndTypes])
    // )

    const createNewAccountType = async (newAccountType) => {
        try {
            const response = await createAccountType(newAccountType);
            setAccountTypes([...accountTypes, response]);
        } catch (error) {
            console.error("Error creating account type", error);
        }
    };

    const updateExistingAccountType = async (updatedAccountType) => {
        try {
            const response = await updateAccountType(updatedAccountType);
            setAccountTypes(accountTypes.map((accountType) => accountType.id === updatedAccountType.id ? response : accountType));
        } catch (error) {
            console.error("Error updating account type", error);
        }
    };

    const removeAccountType = async (accountTypeId) => {
        try {
            await deleteAccountType(accountTypeId);
            setAccountTypes(accountTypes.filter((accountType) => accountType.id !== accountTypeId));
        } catch (error) {
            console.error("Error deleting account type", error);
        }
    };

    const createNewAccount = async (newAccount) => {
        try {
            const response = await createAccount(newAccount);
            setAccounts([...accounts, response]);
            // added
            await fetchAccountsData(); // refresh accounts to update balances
            // end of added
        } catch (error) {
            console.error("Error creating account", error);
        }
    };

    const updateExistingAccount = async (updatedAccount) => {
        try {
            const response = await updateAccount(updatedAccount);
            setAccounts(accounts.map((account) => account.id === updatedAccount.id ? response : account));
            // added
            await fetchAccountsData(); // refresh accounts to update balances
            // end of added
        } catch (error) {
            console.error("Error updating account", error);
        }
    };

    const removeAccount = async (accountId) => {
        try {
            await deleteAccount(accountId);
            setAccounts(accounts.filter((account) => account.id !== accountId));
        } catch (error) {
            console.error("Error deleting account", error);
        }
    };

    return (
        <AccountContext.Provider value={{ accountTypes, accounts, loading, createNewAccount, updateExistingAccount, removeAccount,
        createNewAccountType, updateExistingAccountType, removeAccountType, fetchAccountsData}}>
            {children}
        </AccountContext.Provider>
    );
}